import React, { useState } from 'react';
import { X, Mail, Save, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const EmailCaptureModal = ({ isOpen, onClose, onSave }) => {
  const [email, setEmail] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSave = async () => {
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }

    setIsSaving(true);
    await onSave(email);
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-cyan-500/30 rounded-2xl max-w-md w-full p-8 relative shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center">
            <Save className="w-8 h-8 text-cyan-400" />
          </div>
        </div>

        {/* Content */}
        <h3 className="text-2xl font-bold text-white text-center mb-3">
          Save Your Progress
        </h3>
        <p className="text-gray-400 text-center mb-6">
          Enter your email to save your assessment progress and return anytime
        </p>

        {/* Email Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setIsValid(true);
              }}
              placeholder="your.email@example.com"
              className={`w-full pl-12 pr-4 py-3 bg-gray-800 border ${
                isValid ? 'border-gray-700' : 'border-red-500'
              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`}
            />
          </div>
          {!isValid && (
            <p className="text-red-400 text-sm mt-2">
              Please enter a valid email address
            </p>
          )}
        </div>

        {/* Benefits */}
        <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-300 font-semibold mb-2">Benefits:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-400">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Resume your assessment anytime
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Access your results from any device
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-400">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Receive your compliance report
            </li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Maybe Later
          </button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>
        </div>

        {/* Privacy Note */}
        <p className="text-xs text-gray-500 text-center mt-4">
          We respect your privacy. Your email is only used to save your progress.
        </p>
      </div>
    </div>
  );
};

export default EmailCaptureModal;
